import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  Input
} from '@angular/core';

import {
  ActivatedRoute,
  Router
}                            from '@angular/router';

import {Logger}              from '@/util/logger';
import {ActionService}       from "@/services/action.service";
import {ProjectService}      from "@/services/project.service";
import {ISubdivisionRow}     from "@/screens/administration/i.subdivision.row";
import {IProjectRow} from "@/screens/administration/i.project.row";
import {IOrganizationRow} from "@/screens/administration/i.organization.row";
import {OrganizationService} from "@/services/organization.service";
import {first} from "rxjs/operators";

@Component({
  standalone: false,
  selector:    'subdivision-list-component',
  template: `<div class="project-list-wrapper">

    <h6>Subdivisions</h6>
    <br/>
    <div class="subdivision-switch" *ngFor="let sub of subdivisions">
      <p-inputSwitch [readonly]="!editable"  [(ngModel)]="checkStates['_' + sub.id.toString()]" (onChange)="toggleSub($event, sub.id)"></p-inputSwitch>
      <label class="approve-user-org-label">{{sub.name}}</label>
    </div>

  </div>`
})

/**
 *
 * @class SubdivisionListComponent
 *
 * render a list of the given subdivisions via @input
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2022
 *
 */
export class SubdivisionListComponent implements OnInit, OnDestroy, AfterViewInit {

  /** can be used either with projects or with organizations */
  @Input() mode: string                    = 'organization';

  /** the organization the subs are linked to */
  @Input() org: IOrganizationRow           = null;

  /** the project the subs are linked to */
  @Input() proj: IProjectRow               = null;

  /** can we edit the projects? */
  @Input() editable: boolean               = false;

  /** the set of projects being rendered */
  @Input() subdivisions: ISubdivisionRow[] = [];

  /**
   *  primeng inputswitch has no way to set initial on/off, you have to use a
   *  model...but we have "N" switches, and we don't know what they are in
   *  advance.  So, we have to use a map as the "model".
   *
   */
  public checkStates: object                = {};

  /**
   * standard constructor
   *
   * @param router     in case we need to redirect the page
   * @param actions    the GUI actions we can invoke
   * @param route      the active URL
   * @param prjservice for working with projects
   * @param orgservice for working with organizations
   *
   */
  constructor(
    private router:     Router,
    private actions:    ActionService,
    private route:      ActivatedRoute,
    private prjservice: ProjectService,
    private orgservice: OrganizationService
  ) {

    let slc: SubdivisionListComponent = this;

    slc.refresh();

    Logger.debug(`[SubdivisionListComponent] checked on start: `, slc.checkStates);

  }

  refresh() {

    let slc: SubdivisionListComponent = this;

    /* by default everything is off */

    slc.subdivisions.forEach((item) => {

      slc.checkStates['_' + item.id.toString()] = false;
    });

    /* enable/disable the sub switches based on the current linkage to the project or organization */

    if (slc.proj) {

      slc.proj.subdivisions.forEach((item) => {

        slc.checkStates['_' + item.id.toString()] = true;

      });

    } else if (slc.org) {

      slc.org.subdivisions.forEach((item) => {

        slc.checkStates['_' + item.id.toString()] = true;

      });

    }
  }

  /* on creation subscribe to the alert service so we get updates when alerts happen from anywhere */
  ngOnInit() {

    let slc: SubdivisionListComponent = this;

    slc.refresh();

    Logger.debug(`[SubdivisionListComponent] checked state init: `, slc.checkStates);

  }

  ngAfterViewInit() {


  }

  /* clean up at the end */
  ngOnDestroy() {

  }

  /** link or unlink the given subdivision to the project or organization...depending on the mode.*/
  toggleSub(event, id: number) {

    /* ensure org with 'key' is NOT in the admin list */

    let slc: SubdivisionListComponent = this;
    let sub: ISubdivisionRow          = slc.subdivisions.find(x => x.id == id);

    if (!sub) {

      return;
    }

    let newState: boolean = event.checked;

    /* toggle! */

    if (slc.mode == 'organization') {

      Logger.debug(`[SubdivisionListComponent] toggling (=> ${newState}) on org (${slc.org.key}):`, sub);

      if (newState) {

        /* link */

        slc.orgservice.linkSubdivision(slc.org.id, sub.id)
          .pipe(first())
          .subscribe(
            (data: any) => {

              if (data.result) {

                data = data.result;
              }

              Logger.debug(`[SubdivisionListComponent] linked!`, data);

            },
            (error) => {

              Logger.error(`[SubdivisionListComponent] could not link:`, error);

            });

      } else {

        /* unlink */

        slc.orgservice.unlinkSubdivision(slc.org.id, sub.id)
          .pipe(first())
          .subscribe(
            (data: any) => {

              if (data.result) {

                data = data.result;
              }

              Logger.debug(`[SubdivisionListComponent] unlinked!`, data);

            },
            (error) => {

              Logger.error(`[SubdivisionListComponent] could not unlink:`, error);

            });
      }

    } else if (slc.mode == 'project') {

      Logger.debug(`[SubdivisionListComponent] toggling (=> ${newState}) on project (${slc.proj.key}):`, sub);

      if (newState) {

        /* link */

        slc.prjservice.linkSubdivision(slc.proj.id, sub.id)
          .pipe(first())
          .subscribe(
            (data: any) => {

              if (data.result) {

                data = data.result;
              }

              Logger.debug(`[SubdivisionListComponent] linked!`, data);

            },
            (error) => {

              Logger.error(`[SubdivisionListComponent] could not link:`, error);

            });

      } else {

        /* unlink */

        slc.prjservice.unlinkSubdivision(slc.proj.id, sub.id)
          .pipe(first())
          .subscribe(
            (data: any) => {

              if (data.result) {

                data = data.result;
              }

              Logger.debug(`[SubdivisionListComponent] unlinked!`, data);

            },
            (error) => {

              Logger.error(`[SubdivisionListComponent] could not unlink:`, error);

            });
      }

    }
  }



}
